import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { listProducts } from '@vn-propane/shared/client';
import type { Product, ErrorResponse, UserOrganization } from '@vn-propane/shared/client';
import { Plus, Search, Package, Loader2, ImageIcon } from 'lucide-react';
import { formatPrice, getImageUrl } from '@/lib/utils';

interface OrganizationContext {
  organization: UserOrganization;
}

export default function ProductsLayout() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OrganizationContext>();
  const [products, setProducts] = useState<Product[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  const fetchProducts = useCallback(async () => {
    if (!orgId) return;

    if (isFirstLoad.current) {
      setInitialLoading(true);
    } else {
      setSearching(true);
    }

    try {
      const result = await listProducts({
        path: { org_id: orgId },
        query: debouncedQuery ? { q: debouncedQuery } : undefined,
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('products.failedToLoad'));
        return;
      }

      if (result.data) {
        setProducts(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('products.failedToLoad'));
      console.error(err);
    } finally {
      setInitialLoading(false);
      setSearching(false);
      isFirstLoad.current = false;
    }
  }, [orgId, navigate, debouncedQuery]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  if (initialLoading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{t('products.title')}</h2>
          <p className="text-muted-foreground">
            {t('products.productCount', { count: products.length })}
          </p>
        </div>
        <Button asChild>
          <Link to={`/organizations/${orgId}/products/new`}>
            <Plus className="w-4 h-4 mr-2" />
            {t('products.addProduct')}
          </Link>
        </Button>
      </div>

      <div className="relative">
        {searching ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        )}
        <Input
          placeholder={t('products.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {products.length === 0 && !debouncedQuery ? (
        <div className="text-center py-12 border rounded-lg">
          <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">{t('products.noProductsYet')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('products.addYourFirstProduct')}
          </p>
          <Button asChild>
            <Link to={`/organizations/${orgId}/products/new`}>
              <Plus className="w-4 h-4 mr-2" />
              {t('products.addProduct')}
            </Link>
          </Button>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">{t('products.noResultsFound')}</h3>
          <p className="text-muted-foreground">
            {t('products.tryDifferentSearch')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => {
            const imageSrc = getImageUrl(product.imageUrl);
            return (
              <Link
                key={product.id}
                to={`/organizations/${orgId}/products/${product.id}`}
                className="flex gap-3 p-4 border rounded-lg hover:border-primary hover:bg-muted/50 transition-colors"
              >
                <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-lg mb-1 truncate">{product.name}</div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {product.sku && (
                      <div>{t('products.sku')}: {product.sku}</div>
                    )}
                    <div>{t('products.unit')}: {product.unit}</div>
                    {product.price && (
                      <div className="text-foreground font-medium">
                        {formatPrice(product.price, organization.currency)}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
